// The page: xterm.js with the HOTTY addon, running engram-site compiled to
// WebAssembly, which prints engram.neuroplast.io into the terminal as HTML.
// The program's output goes to the terminal; the terminal's input (keys,
// and the addon's replies) goes to the program. cmd/engram-site is the Go
// half of this bridge, as kubecom's is kubecom.neuroplast.io's.
//
// No function the Go program can reach may throw: syscall/js turns a JS
// exception into a Go panic that kills it. Every host method is guarded.
//
// The terminal is the size of the window, and the page is one surface in
// it that the addon scrolls natively (HOTTY's scroll, SPEC §5.3): the
// wheel, touch and the keys move the surface's document, and neither the
// terminal nor the window scrolls.
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { UnicodeGraphemesAddon } from "@xterm/addon-unicode-graphemes";
import { HottyAddon } from "@neuroplastio/xterm-addon-hotty";

declare const Go: new () => { importObject: WebAssembly.Imports; run(i: WebAssembly.Instance): Promise<void> };
declare global {
  interface Window {
    siteHost: Record<string, unknown>;
    site?: { write(b: Uint8Array): void; resize(cols: number, rows: number): void };
    // For tests: whether the program started, how many times it has
    // printed the page and placed it again, and what failed.
    page: { term: Terminal; ready: boolean; prints: number; places: number; failed?: string };
  }
}

const status = document.getElementById("status")!;
const termEl = document.getElementById("term")!;
const term = new Terminal({
  // term.unicode, which the Unicode addon sets.
  allowProposedApi: true,
  fontFamily: '"IBM Plex Mono", "JetBrains Mono", "DejaVu Sans Mono", ui-monospace, monospace',
  fontSize: matchMedia("(max-width: 520px)").matches ? 12 : 15,
  lineHeight: 1.15,
  cursorBlink: false,
  scrollback: 0,
  theme: {
    background: "#12121a",
    foreground: "#d7d7e0",
    cursor: "#9d90ff",
    selectionBackground: "#5b4bd655",
    black: "#12121a", red: "#f07a7a", green: "#7fd4a0", yellow: "#e8c872",
    blue: "#8fa8ff", magenta: "#9d90ff", cyan: "#7fd4d4", white: "#d7d7e0",
    brightBlack: "#5b6270", brightRed: "#ff9a9a", brightGreen: "#9fe4b8", brightYellow: "#f2d892",
    brightBlue: "#aec0ff", brightMagenta: "#b9afff", brightCyan: "#9fe4e4", brightWhite: "#ffffff",
  },
});
const fit = new FitAddon();
// Hyperlinks in surfaces (HOTTY SPEC §9) go where OSC 8 links go: a new
// tab without an opener, not xterm.js's confirm dialog.
term.options.linkHandler = {
  activate: (_e, uri) => void window.open(uri, "_blank", "noopener,noreferrer"),
};
const hotty = new HottyAddon({
  onInvalid: (reason) => console.warn("engram-site: the addon dropped a HOTTY message:", reason),
  // The font hotty hosts ship, which build.mjs serves from the addon:
  // what the terminal's font lacks (Nerd Font icons, keys' symbols)
  // draws in it.
  fonts: "/xterm-addon-hotty/fonts/",
});
term.loadAddon(fit);
term.loadAddon(hotty);
// A character takes the columns the program counts: grapheme clusters at
// Unicode 15's widths (an emoji, ⌨️ with its VS16, a ZWJ sequence: two), as
// the kit's cells and native terminals measure them, not xterm.js's own
// Unicode 6, where an emoji takes one and covers the column after it.
term.loadAddon(new UnicodeGraphemesAddon());
// The DOM renderer: the cells are nearly all under the page's surface.
term.open(termEl);
fit.fit();
window.page = { term, ready: false, prints: 0, places: 0 };

function guard<A extends unknown[]>(name: string, fn: (...a: A) => void) {
  return (...a: A) => {
    try {
      fn(...a);
    } catch (e) {
      console.error(`engram-site: ${name} threw`, e);
    }
  };
}

function fail(msg: string) {
  window.page.failed = msg;
  status.textContent = `The page stopped: ${msg}`;
  status.hidden = false;
}

/**
 * The terminal resizes only when xterm.js has parsed all it was given, and
 * nothing waits behind: a resize parses what is queued synchronously, and a
 * HOTTY message the addon decodes asynchronously (a compressed one) cannot be
 * parsed so, which stops xterm.js for good. So the program's output is held
 * from the time a resize is asked for until it is done.
 */
let resizes = 0;
let held: (Uint8Array | (() => void))[] = [];
function output(bytes: Uint8Array | (() => void)) {
  if (resizes > 0) held.push(bytes);
  else if (typeof bytes === "function") term.write("", bytes);
  else term.write(bytes);
}
function resizing(fn: () => void) {
  resizes++;
  term.write("", () =>
    setTimeout(() => {
      try {
        fn();
      } catch (e) {
        console.warn("engram-site: resize dropped", e);
      }
      if (--resizes === 0) {
        const out = held;
        held = [];
        for (const b of out) output(b);
      }
    }),
  );
}

window.siteHost = {
  cols: term.cols,
  rows: term.rows,
  origin: location.origin,
  output: guard("output", output),
  ready: guard("ready", () => {
    status.hidden = true;
    window.page.ready = true;
    window.site?.resize(term.cols, term.rows);
  }),
  // The page is shown (for tests), once xterm.js has parsed it: it parses
  // writes later, in order, after any it holds.
  printed: guard("printed", () => output(() => window.page.prints++)),
  // And placed again, at another size.
  placed: guard("placed", () => output(() => window.page.places++)),
  failed: guard("failed", (msg: string) => fail(msg)),
};

const encoder = new TextEncoder();
term.onData((data) => {
  try {
    window.site?.write(encoder.encode(data));
  } catch (e) {
    console.warn("engram-site: input dropped", e);
  }
});

/**
 * Another window size: the terminal takes it, and the program places the
 * page again.
 */
function resize() {
  resizing(() => {
    const dims = fit.proposeDimensions();
    if (!dims || !dims.cols || !dims.rows || (dims.cols === term.cols && dims.rows === term.rows)) return;
    term.resize(dims.cols, dims.rows);
    window.site?.resize(term.cols, term.rows);
  });
}
addEventListener("resize", resize);

const go = new Go();
WebAssembly.instantiateStreaming(fetch("engram-site.wasm"), go.importObject)
  .then((r) => go.run(r.instance))
  .catch((e) => {
    console.error("engram-site: WebAssembly failed", e);
    fail(String(e));
  });
